import { useCallback } from "react";
import { useNavigate } from "react-router";
import { AnimatePresence, motion } from "framer-motion";
import { SlideShell } from "@components/SlideShell";
import { useCustomer } from "@contexts/CustomerContext";
import { useCustomerData } from "@contexts/CustomerContext";
import { SlideProvider, useSlideContext } from "@contexts/SlideContext";
import { useSlideNavigation } from "@hooks/useSlideNavigation";
import { useFullscreen } from "@hooks/useFullscreen";
import { slideComponents } from "@slides/index";
import type { SlideDefinition } from "@data/types";

function SlideRenderer({ slides }: { slides: SlideDefinition[] }) {
  const customer = useCustomer();
  const navigate = useNavigate();
  const { exitFullscreen } = useFullscreen();
  const { currentSlide } = useSlideContext();

  const handleExit = useCallback(async () => {
    await exitFullscreen();
    navigate(`/${customer.id}/onsite`);
  }, [exitFullscreen, navigate, customer.id]);

  useSlideNavigation({ onExit: handleExit });

  const slide = slides[currentSlide];
  if (!slide) return null;

  const Component = slideComponents[slide.component];
  if (!Component) return null;

  return (
    <SlideShell
      colorFrom={customer.theme.pathColorFrom}
      colorTo={customer.theme.pathColorTo}
      onExit={handleExit}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={slide.id}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.3 }}
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Component {...slide.props} />
        </motion.div>
      </AnimatePresence>
    </SlideShell>
  );
}

export function SlidesPage() {
  const { slides } = useCustomerData();

  return (
    <SlideProvider totalSlides={slides.length}>
      <SlideRenderer slides={slides} />
    </SlideProvider>
  );
}
